import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Film, Upload, Trash2, Pencil, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';
import { videoIzleUrl } from '@/components/EgitimVideolari';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHUNK_SIZE = 5 * 1024 * 1024;
const formatBoyut = (b) => `${(b / 1024 / 1024).toFixed(1)} MB`;

export const VideoYonetimi = () => {
  const [videolar, setVideolar] = useState([]);
  const [duzenle, setDuzenle] = useState(null);
  const [onizle, setOnizle] = useState(null);

  const yenile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/videolar`);
      setVideolar(data);
    } catch (e) {
      toast.error('Videolar yüklenemedi');
    }
  }, []);

  useEffect(() => { yenile(); }, [yenile]);

  const sirala = async (index, yon) => {
    const yeni = [...videolar];
    const hedef = index + yon;
    if (hedef < 0 || hedef >= yeni.length) return;
    [yeni[index], yeni[hedef]] = [yeni[hedef], yeni[index]];
    setVideolar(yeni);
    try {
      await axios.put(`${API}/videolar/sirala`, { ids: yeni.map((v) => v.id) });
    } catch (e) {
      toast.error('Sıralama kaydedilemedi');
      yenile();
    }
  };

  const sil = async (v) => {
    if (!window.confirm(`"${v.baslik}" videosu silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/videolar/${v.id}`);
      toast.success('Video silindi');
      yenile();
    } catch (e) {
      toast.error('Silme başarısız');
    }
  };

  return (
    <Card data-testid="video-yonetimi">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Film className="w-5 h-5" /> Eğitim Videoları (Giriş Sayfası)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <VideoYukleForm onDone={yenile} />
        {videolar.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="video-yok">Henüz video yüklenmedi.</p>
        ) : (
          <ul className="divide-y border rounded-md" data-testid="video-yonetim-listesi">
            {videolar.map((v, i) => (
              <li key={v.id} className="flex items-center gap-3 p-3" data-testid={`video-yonetim-item-${i}`}>
                <div className="flex flex-col">
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === 0} onClick={() => sirala(i, -1)} data-testid={`video-yukari-${i}`}><ArrowUp className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-6 w-6" disabled={i === videolar.length - 1} onClick={() => sirala(i, 1)} data-testid={`video-asagi-${i}`}><ArrowDown className="w-4 h-4" /></Button>
                </div>
                <button className="flex-1 min-w-0 text-left" onClick={() => setOnizle(v)} title="Önizle">
                  <div className="font-medium text-slate-800 truncate">{i + 1}. {v.baslik}</div>
                  <div className="text-xs text-gray-500 truncate">{v.dosya_adi} • {formatBoyut(v.boyut)}{v.aciklama ? ` • ${v.aciklama}` : ''}</div>
                </button>
                <Button variant="outline" size="sm" onClick={() => setDuzenle(v)} data-testid={`video-duzenle-${i}`}><Pencil className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => sil(v)} data-testid={`video-sil-${i}`}><Trash2 className="w-4 h-4" /></Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {duzenle && <VideoDuzenleDialog video={duzenle} onClose={() => setDuzenle(null)} onDone={() => { setDuzenle(null); yenile(); }} />}
      {onizle && (
        <Dialog open onOpenChange={(o) => !o && setOnizle(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>{onizle.baslik}</DialogTitle></DialogHeader>
            <video src={videoIzleUrl(onizle.id)} controls autoPlay className="w-full aspect-video bg-black rounded-md" />
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

const VideoYukleForm = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [baslik, setBaslik] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [progress, setProgress] = useState(null);

  const yukle = async () => {
    if (!file) return toast.error('Lütfen bir video dosyası seçin');
    if (file.size > 500 * 1024 * 1024) return toast.error('Video 500 MB\'ı aşamaz');
    const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
    const total = Math.ceil(file.size / CHUNK_SIZE);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        fd.append('dosya_adi', file.name);
        fd.append('baslik', baslik);
        fd.append('aciklama', aciklama);
        fd.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await axios.post(`${API}/videolar/chunk`, fd, { timeout: 15 * 60 * 1000 });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success('Video yüklendi');
      setFile(null); setBaslik(''); setAciklama('');
      document.getElementById('video-file-input').value = '';
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setProgress(null);
    }
  };

  const yukleniyor = progress !== null;
  return (
    <div className="border rounded-md p-4 bg-slate-50 space-y-3" data-testid="video-yukle-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="video-file-input">Video dosyası (mp4, webm, mov — en fazla 500 MB)</Label>
          <Input id="video-file-input" type="file" accept="video/*,.mp4,.webm,.mov,.m4v,.mkv" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={yukleniyor} data-testid="video-file-input" />
        </div>
        <div>
          <Label htmlFor="video-baslik">Başlık</Label>
          <Input id="video-baslik" placeholder="örn. 1. Müşteri Ekleme" value={baslik} onChange={(e) => setBaslik(e.target.value)} maxLength={120} disabled={yukleniyor} data-testid="video-baslik-input" />
        </div>
      </div>
      <div>
        <Label htmlFor="video-aciklama">Kısa açıklama</Label>
        <Input id="video-aciklama" placeholder="Bu videoda neler anlatılıyor?" value={aciklama} onChange={(e) => setAciklama(e.target.value)} maxLength={500} disabled={yukleniyor} data-testid="video-aciklama-input" />
      </div>
      {yukleniyor && (
        <div data-testid="video-progress">
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${progress}%` }} /></div>
          <p className="text-xs text-gray-600 mt-1">Yükleniyor… %{progress}{progress === 100 ? ' — depolamaya aktarılıyor' : ''}</p>
        </div>
      )}
      <Button onClick={yukle} disabled={yukleniyor || !file} className="bg-emerald-600 hover:bg-emerald-700" data-testid="video-yukle-btn">
        {yukleniyor ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        Video Yükle
      </Button>
    </div>
  );
};

const VideoDuzenleDialog = ({ video, onClose, onDone }) => {
  const [baslik, setBaslik] = useState(video.baslik);
  const [aciklama, setAciklama] = useState(video.aciklama || '');
  const [loading, setLoading] = useState(false);

  const kaydet = async () => {
    if (!baslik.trim()) return toast.error('Başlık boş olamaz');
    setLoading(true);
    try {
      await axios.put(`${API}/videolar/${video.id}`, { baslik, aciklama });
      toast.success('Video güncellendi');
      onDone();
    } catch (e) {
      toast.error('Güncelleme başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="video-duzenle-dialog">
        <DialogHeader><DialogTitle>Videoyu Düzenle</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div><Label>Başlık</Label><Input value={baslik} onChange={(e) => setBaslik(e.target.value)} maxLength={120} data-testid="video-duzenle-baslik" /></div>
          <div><Label>Kısa açıklama</Label><Input value={aciklama} onChange={(e) => setAciklama(e.target.value)} maxLength={500} data-testid="video-duzenle-aciklama" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>İptal</Button>
          <Button onClick={kaydet} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="video-duzenle-kaydet">Kaydet</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
